import React from "react";
import { AuthSession, fetchAuthSession } from "@aws-amplify/auth";
import { FsAuthenticator, useAuthSession } from "../../src/components/auth";

export interface AuthenticatedUserState {
  isAuthenticated: boolean;
  isUserLoading: boolean;
  authenticatedUser: any;
  currentSession?: AuthSession;
  isHost: boolean;
  isAdmin: boolean;
  signOut: () => void;
}

const initialUserState: AuthenticatedUserState = {
  isAuthenticated: false,
  isUserLoading: true,
  authenticatedUser: undefined,
  currentSession: undefined,
  isHost: false,
  isAdmin: false,
  signOut: () => {},
};

const AuthenticatedUserStateContext =
  React.createContext<AuthenticatedUserState>(initialUserState);

// Inner component — rendered only when FsAuthenticator has confirmed sign-in
const AuthenticatedInner: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, userId, email, groups, signOut } = useAuthSession();
  const [currentSession, setCurrentSession] = React.useState<AuthSession>();

  React.useEffect(() => {
    if (!isAuthenticated) return;
    fetchAuthSession({ clientMetadata: { name: "apps-admin-portal" } })
      .then(setCurrentSession)
      .catch((err) => console.error("Unable to fetch session", err));
  }, [isAuthenticated]);

  const isHost = groups.includes("Hosts");
  const isAdmin = groups.includes("Admins");

  const userState: AuthenticatedUserState = {
    isAuthenticated,
    isUserLoading: false,
    authenticatedUser: { userId, email },
    currentSession,
    isHost,
    isAdmin,
    signOut,
  };

  return (
    <AuthenticatedUserStateContext.Provider value={userState}>
      {children}
    </AuthenticatedUserStateContext.Provider>
  );
};

// Mock provider for local development (bypasses FsAuthenticator)
const MockAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const mockUserState: AuthenticatedUserState = {
    isAuthenticated: true,
    isUserLoading: false,
    authenticatedUser: {
      username: "local-dev-user",
      attributes: {
        sub: "local-dev-user-id",
        email: "dev@localhost.com",
      },
    },
    currentSession: undefined,
    isHost: false,
    isAdmin: true, // Mock user is always admin
    signOut: () => console.log("Mock sign out"),
  };

  return (
    <AuthenticatedUserStateContext.Provider value={mockUserState}>
      {children}
    </AuthenticatedUserStateContext.Provider>
  );
};

export const AuthenticatedUserProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  // Use mock auth ONLY in local development
  const useMockAuth =
    process.env.REACT_APP_USE_MOCK_AUTH === "true" &&
    process.env.NODE_ENV === "development";

  if (useMockAuth) {
    return <MockAuthProvider>{children}</MockAuthProvider>;
  }

  return (
    <FsAuthenticator>
      <AuthenticatedInner>{children}</AuthenticatedInner>
    </FsAuthenticator>
  );
};

export const useAuthenticatedUserState = () => {
  const context = React.useContext(AuthenticatedUserStateContext);

  if (!context) {
    throw new Error(
      "useAuthenticatedUserState must be used within AuthenticatedUserStateProvider",
    );
  }

  return context;
};
